import React from "react";

/**
 * SubscriptionResult (no external CSS frameworks)
 * - Inline styles only, works out of the box
 * - Pass `complete={true}` for success; `false` for incomplete
 * - Optional URLs can be overridden via props
 */
const SubscriptionResult = ({
  complete,
  chatUrl = "/",
  supportEmail = "matt@dealcompass.co",
  profileUrl = "/profile",
}) => {
  const title = complete ? "Subscription Active" : "Subscription Incomplete";
  const subtitle = complete
    ? "You're all set! Your plan is now active."
    : "Looks like checkout didn’t finish. You can try again or contact us if you need help.";

  // inline SVG icons (no deps)
  const CheckIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <path d="m22 4-10 10-3-3" />
    </svg>
  );
  const XIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );

  // styles
  const styles = {
    page: {
      minHeight: "100vh",
      minWidth: "100vw",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      background: "linear-gradient(to bottom, #f8fafc, #ffffff)", // slate-50 → white
      fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      color: "#0f172a", // slate-900
    },
    wrap: { width: "100%", maxWidth: 720 },
    card: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 16,
      background: "#ffffff",
      boxShadow:
        "0 1px 2px rgba(0,0,0,0.05), 0 8px 24px rgba(15,23,42,0.08)",
      border: "1px solid #e2e8f0", // slate-200
    },
    bar: {
      height: 6,
      width: "100%",
      background: complete
        ? "linear-gradient(90deg, #34d399, #10b981, #059669)" // emeralds
        : "linear-gradient(90deg, #f59e0b, #ef4444, #dc2626)", // amber→rose
    },
    inner: { padding: "28px 28px 24px", textAlign: "center" },
    iconWrap: {
      height: 56,
      width: 56,
      margin: "0 auto 12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "9999px",
      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.06)",
      border: "1px solid #e2e8f0",
      color: complete ? "#10b981" : "#ef4444",
    },
    title: { fontSize: 20, fontWeight: 700, letterSpacing: 0.2 },
    subtitle: { marginTop: 8, color: "#475569" }, // slate-600
    grid: (cols) => ({
      marginTop: 24,
      display: "grid",
      gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`,
      gap: 12,
    }),
    btn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 12,
      padding: "10px 16px",
      textDecoration: "none",
      fontWeight: 600,
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      transition: "transform 120ms ease, box-shadow 120ms ease",
      cursor: "pointer",
      border: "1px solid transparent",
    },
    btnPrimary: {
      background: "#0f172a",
      color: "#ffffff",
    },
    btnSecondary: {
      background: "#f1f5f9",
      borderColor: "#e2e8f0",
      color: "#0f172a",
    },
    finePrint: { marginTop: 20, fontSize: 12, color: "#64748b" },
    blurb: { marginTop: 16, textAlign: "center", fontSize: 14, color: "#64748b" },
    arrow: { marginLeft: 8 },
  };

  // Helpers to compose button styles
  const btn = (base, extra = {}) => ({ ...styles.btn, ...base, ...extra });

  return (
    <div style={styles.page}>
      <div style={styles.wrap}>
        <div style={styles.card}>
          <div style={styles.bar} />

          <div style={styles.inner}>
            <div style={styles.iconWrap} aria-hidden>
              {complete ? (
                <CheckIcon width={28} height={28} />
              ) : (
                <XIcon width={28} height={28} />
              )}
            </div>

            <h1 style={styles.title}>{title}</h1>
            <p style={styles.subtitle}>{subtitle}</p>

            {complete ? (
              <div style={styles.grid(2)}>
                <a href={chatUrl} style={btn(styles.btnPrimary)}>
                  Go to Chat <span style={styles.arrow}>→</span>
                </a>
                <a href={profileUrl} style={btn(styles.btnSecondary)}>
                  Manage Billing <span style={styles.arrow}>↗</span>
                </a>
              </div>
            ) : (
              <div style={styles.grid(2)}>
                <a href={chatUrl} style={btn(styles.btnSecondary)}>
                  Back to chat
                </a>
                <a href={`mailto:${supportEmail}`} style={btn(styles.btnPrimary, { background: "#dc2626" })}>
                    Contact support<span style={styles.arrow}>→</span>
                </a>

              </div>
            )}

            <div style={styles.finePrint}>
              {complete ? (
                <p>
                  If this wasn’t you or you need help, contact {" "}
                  <a href={`mailto:${supportEmail}`} style={{ textDecoration: "underline", color: "inherit" }}>
                    {supportEmail}
                  </a>.
                </p>
              ) : (
                <p>
                  Need help finishing checkout? We’re here: {" "}
                  <a href={`mailto:${supportEmail}`} style={{ textDecoration: "underline", color: "inherit" }}>
                    {supportEmail}
                  </a>.
                </p>
              )}
            </div>
          </div>
        </div>

        <div style={styles.blurb}>
          {complete ? (
            <p>
              Pro tip: bookmark <a href={chatUrl} style={{ textDecoration: "underline", color: "inherit" }}>your dashboard</a> for quick access.
            </p>
          ) : (
            <p>You can safely close this tab—your cart will remain available for a short time.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionResult;
